import { useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import { ModalDialog } from 'core-components/library/modalDialog'
import { TKeyInitiative } from 'partner360/entities/keyInitiatives'
import { scoreCards } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import type { IScoreCard } from 'partner360/features/keyInitiatives/model/types'
import { ScoreCard } from 'partner360/features/keyInitiatives/ui/common/scoreCard/ScoreCard'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { QualityPillar } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/components/qualityPillar/QualityPillar'
import { getInitiativesPlanQuality } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/helpers/getInitiativesPlanQuality'
import { useDescriptionQualityColumns } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useDescriptionQualityColumns'
import { useFinancialTableConfig } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useFinancialColumns'

import { StrategicAlignmentPillar } from './components/strategicAlignmentPillar/StrategicAlignmentPillar'

import styles from './KeyInitiativesQualityContent.module.scss'

type TInitiativesPlanQuality = ReturnType<typeof getInitiativesPlanQuality>

interface IKeyInitiativesQualityContentProps {
  quality: TInitiativesPlanQuality
  activeCurrency: string
  canEdit?: boolean
  onEditInitiative?: (initiative: TKeyInitiative) => void
  onCreateIncrementalInitiative?: () => void
  tourId?: string
}

const noEditor = () => undefined

/**
 * The reusable, chart-free part of Initiatives Quality. The planning screen can opt into row
 * editing and quick-filter navigation; review/read-only consumers receive the same scores,
 * explanations and detail tables without inheriting planning-only actions.
 */
export const KeyInitiativesQualityContent = ({
  quality,
  activeCurrency,
  canEdit = false,
  onEditInitiative,
  onCreateIncrementalInitiative,
  tourId,
}: IKeyInitiativesQualityContentProps) => {
  const [dialogScoreCard, setDialogScoreCard] = useState<IScoreCard | null>(null)
  const [isFinCoverageDetailsVisible, setIsFinCoverageDetailsVisible] = useState(false)
  const [isDescriptionQualityDetailsVisible, setIsDescriptionQualityDetailsVisible] =
    useState(false)

  const { financialTargetGapColumns, financialTargetGapRowActions } = useFinancialTableConfig({
    activeCurrency,
    onEditInitiative: onEditInitiative ?? noEditor,
  })
  const { descriptionQualityGapRowActions, descriptionQualityGapColumns } =
    useDescriptionQualityColumns({
      onEditInitiative: onEditInitiative ?? noEditor,
    })

  return (
    <>
      <div className={styles.pillars} data-tour-id={tourId}>
        <QualityPillar
          title={TRANSLATION_KEYS.quality.financialPlanCoverage.title}
          score={quality.financialPlanCoverage.score}
          isNotApplicable={quality.financialPlanCoverage.score === null}
          notApplicableText={TRANSLATION_KEYS.quality.financialPlanCoverage.notApplicable}
          band={quality.financialPlanCoverage.band}
          description={TRANSLATION_KEYS.quality.financialPlanCoverage.description}
          descriptionReplaceParams={[
            {
              key: '{goalCoverage}',
              value: String(quality.financialPlanCoverage.goalCoverage),
            },
            {
              key: '{incrementalCoverage}',
              value: String(quality.financialPlanCoverage.incrementalCoverage),
            },
            {
              key: '{IncrementalPlanSum}',
              value: formatMoney(quality.financialPlanCoverage.incrementalPlanSum, activeCurrency),
            },
            {
              key: '{IncrementalInitiativesSum}',
              value: formatMoney(
                quality.financialPlanCoverage.incrementalInitiativesSum,
                activeCurrency
              ),
            },
          ]}
          actionText={
            quality.financialPlanCoverage.initiativesWithIncremental.length > 0
              ? isFinCoverageDetailsVisible
                ? TRANSLATION_KEYS.quality.actions.hideDetailsFin
                : TRANSLATION_KEYS.quality.actions.showDetailsFin
              : undefined
          }
          actionReplaceParams={[
            {
              key: '{count}',
              value: String(quality.financialPlanCoverage.initiativesWithIncremental.length),
            },
          ]}
          isActionActive={isFinCoverageDetailsVisible}
          onAction={() => setIsFinCoverageDetailsVisible((value) => !value)}
          onHowScore={() => setDialogScoreCard(scoreCards[0])}
          leadingAction={
            canEdit && onCreateIncrementalInitiative ? (
              <Button onClick={onCreateIncrementalInitiative}>
                <Translation text={TRANSLATION_KEYS.quality.actions.createIncrementalInitiative} />
              </Button>
            ) : undefined
          }
        >
          {isFinCoverageDetailsVisible && (
            <div className={styles.detailsTable}>
              <Table
                items={quality.financialPlanCoverage.initiativesWithIncremental}
                columns={financialTargetGapColumns}
                keyField={'EntryId'}
                header
                rowActions={canEdit ? financialTargetGapRowActions : undefined}
                rowActionsVisible={canEdit ? 'visible' : 'hidden'}
              />
            </div>
          )}
        </QualityPillar>

        <QualityPillar
          title={TRANSLATION_KEYS.quality.descriptionQuality.title}
          score={quality.descriptionQuality.score}
          band={quality.descriptionQuality.band}
          description={TRANSLATION_KEYS.quality.descriptionQuality.description}
          descriptionReplaceParams={[
            {
              key: '{wellDescribedCount}',
              value: String(quality.descriptionQuality.wellDescribedCount),
            },
            {
              key: '{totalCount}',
              value: String(quality.descriptionQuality.totalCount),
            },
          ]}
          successText={
            quality.descriptionQuality.lowScoredCount === 0
              ? TRANSLATION_KEYS.quality.descriptionQuality.success
              : undefined
          }
          actionText={
            quality.descriptionQuality.lowScoredCount > 0
              ? isDescriptionQualityDetailsVisible
                ? TRANSLATION_KEYS.quality.actions.hideDetailsDesc
                : TRANSLATION_KEYS.quality.actions.showDetailsDesc
              : undefined
          }
          actionReplaceParams={[
            {
              key: '{count}',
              value: String(quality.descriptionQuality.lowScoredCount),
            },
          ]}
          isActionActive={isDescriptionQualityDetailsVisible}
          onAction={() => setIsDescriptionQualityDetailsVisible((value) => !value)}
          onHowScore={() => setDialogScoreCard(scoreCards[1])}
        >
          {isDescriptionQualityDetailsVisible && (
            <div className={styles.detailsTable}>
              <Table
                items={quality.descriptionQuality.lowScoredInitiatives}
                columns={descriptionQualityGapColumns}
                keyField={'EntryId'}
                header
                rowActions={canEdit ? descriptionQualityGapRowActions : undefined}
                rowActionsVisible={canEdit ? 'visible' : 'hidden'}
              />
            </div>
          )}
        </QualityPillar>

        <StrategicAlignmentPillar
          quality={quality.strategicGrowthAreas}
          onEditInitiative={onEditInitiative}
          canEdit={canEdit}
        />
      </div>

      <ModalDialog
        show={dialogScoreCard !== null}
        onClose={() => setDialogScoreCard(null)}
        contentProps={{ scoreCard: dialogScoreCard }}
        content={ScoreCard}
        size={'xs'}
      />
    </>
  )
}
